/**
 * Scorecard Cell Overlays Alpine Component
 * Manages both the deeplink icon and drilldown 3-dots menu overlays for scorecard cells.
 * Uses a single set of mouse event listeners to track cell hover and show/hide both overlays.
 */

import { DRILLDOWN_EVENT, returnFocusAfterDrilldown } from '../charts/cell-actions.js';
import { workspaceDateString } from '../workspace-today';

// Module-level state — assumes a single scorecard view is active at a time.
// The overlay elements (deeplink anchor, drilldown menu) are singletons appended
// to document.body and repositioned on hover. Do not instantiate multiple components
// that call initCellOverlaysFeature() concurrently.
let state = {
    // Shared
    currentHoveredCell: null,
    initialized: false,
    intentConfirmed: false,
    intentTimeout: null,
    hideTimeout: null,
    boundHandleCellMouseOver: null,
    boundHandleCellMouseOut: null,
    boundHandleScroll: null,
    INTENT_DELAY: 200,
    HIDE_DELAY: 100,

    // Deeplink
    deeplinkElement: null,
    currentDeeplink: null,

    // Drilldown menu
    menuElement: null,
    triggerElement: null,
    dropdownElement: null,
    menuOpen: false,
    boundHandleClickOutside: null,
    boundHandleKeydown: null,
};

export default (strings = {}) => ({
    _co_strings: {
        options: strings.options || 'Options',
        cellOptions: strings.cellOptions || 'Cell options',
        drilldownToDetails: strings.drilldownToDetails || 'Drilldown to Details',
        openSourceLink: strings.openSourceLink || 'Open source link',
    },
    initCellOverlaysFeature() {
        if (state.initialized) {
            this.destroyCellOverlaysFeature();
        }
        this._co_createDeeplinkElement();
        this._co_createMenuElement();
        this._co_setupEventListeners();
        state.initialized = true;
    },

    destroyCellOverlaysFeature() {
        if (!state.initialized) return;

        if (state.boundHandleCellMouseOver) {
            document.body.removeEventListener('mouseover', state.boundHandleCellMouseOver);
        }
        if (state.boundHandleCellMouseOut) {
            document.body.removeEventListener('mouseout', state.boundHandleCellMouseOut);
        }
        if (state.boundHandleScroll) {
            document.removeEventListener('scroll', state.boundHandleScroll, true);
        }
        if (state.boundHandleClickOutside) {
            document.removeEventListener('mousedown', state.boundHandleClickOutside);
        }
        if (state.boundHandleKeydown) {
            document.removeEventListener('keydown', state.boundHandleKeydown);
        }

        if (state.deeplinkElement) state.deeplinkElement.remove();
        if (state.menuElement) state.menuElement.remove();

        this._co_clearTimeouts();

        state.currentHoveredCell = null;
        state.currentDeeplink = null;
        state.deeplinkElement = null;
        state.menuElement = null;
        state.triggerElement = null;
        state.dropdownElement = null;
        state.initialized = false;
        state.menuOpen = false;
        state.intentConfirmed = false;
        state.boundHandleCellMouseOver = null;
        state.boundHandleCellMouseOut = null;
        state.boundHandleScroll = null;
        state.boundHandleClickOutside = null;
        state.boundHandleKeydown = null;
    },

    refreshCellOverlaysFeature() {
        this._co_hideAll();
    },

    hideCellOverlaysFeature() {
        this._co_hideAll();
    },

    // Public helper used by blade templates for data-deeplink attribute
    dl_getCellDeeplink(rowData, headerData) {
        if (headerData.isPlaceholder || !this.activeInterval) return null;

        const metric = this.fullScorecardData?.metrics?.[rowData.metric_id];
        if (!metric) return null;

        const data = metric.data?.[this.activeInterval];
        if (!data || !data[headerData.start_date]) return null;

        return data[headerData.start_date].deeplink || null;
    },

    // ── Deeplink element creation ──

    _co_createDeeplinkElement() {
        let link = document.getElementById('scorecard-deeplink-overlay');
        if (!link) {
            link = document.createElement('a');
            link.id = 'scorecard-deeplink-overlay';
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.className = 'fixed pointer-events-none opacity-0 transition-opacity duration-150 z-10 cursor-pointer';
            link.setAttribute('aria-label', this._co_strings.openSourceLink || 'Open source link');
            link.style.cssText = 'will-change: opacity, top, left;';

            link.innerHTML = `
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" style="pointer-events: none;" aria-hidden="true">
                    <path d="M11.0013 0.916627C9.00701 0.916627 7.0575 1.508 5.39931 2.61598C3.74111 3.72395 2.4487 5.29875 1.68552 7.14124C0.922338 8.98372 0.722654 11.0111 1.11172 12.9671C1.50079 14.9231 2.46113 16.7198 3.87131 18.13C5.28149 19.5401 7.07817 20.5005 9.03415 20.8895C10.9901 21.2786 13.0175 21.0789 14.86 20.3157C16.7025 19.5526 18.2773 18.2602 19.3853 16.602C20.4933 14.9438 21.0846 12.9943 21.0846 11C21.0854 9.6756 20.825 8.36407 20.3186 7.14038C19.8121 5.91669 19.0694 4.80482 18.1329 3.86836C17.1964 2.93189 16.0846 2.18918 14.8609 1.6827C13.6372 1.17622 12.3257 0.915905 11.0013 0.916627ZM12.2938 14.8866L11.6521 15.5375C10.9641 16.2255 10.031 16.612 9.05797 16.612C8.08497 16.612 7.15182 16.2255 6.46381 15.5375C5.77579 14.8494 5.38927 13.9163 5.38927 12.9433C5.38927 11.9703 5.77579 11.0371 6.46381 10.3491L7.11464 9.70746C7.29 9.55729 7.51557 9.47881 7.74627 9.48773C7.97698 9.49664 8.19582 9.59228 8.35907 9.75553C8.52233 9.91878 8.61796 10.1376 8.62687 10.3683C8.63579 10.599 8.55731 10.8246 8.40714 11L7.75631 11.6508C7.4123 11.9948 7.21904 12.4614 7.21904 12.9479C7.21904 13.4344 7.4123 13.901 7.75631 14.245C8.10031 14.589 8.56689 14.7822 9.05339 14.7822C9.53989 14.7822 10.0065 14.589 10.3505 14.245L11.0013 13.5941C11.1767 13.444 11.4022 13.3655 11.6329 13.3744C11.8636 13.3833 12.0825 13.4789 12.2457 13.6422C12.409 13.8054 12.5046 14.0243 12.5135 14.255C12.5225 14.4857 12.444 14.7113 12.2938 14.8866ZM12.6238 10.6791L10.6805 12.6225C10.5079 12.7951 10.2737 12.892 10.0296 12.892C9.78553 12.892 9.55142 12.7951 9.37881 12.6225C9.20619 12.4499 9.10922 12.2157 9.10922 11.9716C9.10922 11.7275 9.20619 11.4934 9.37881 11.3208L11.3221 9.37746C11.4948 9.20485 11.7289 9.10788 11.973 9.10788C12.2171 9.10788 12.4512 9.20485 12.6238 9.37746C12.7964 9.55007 12.8934 9.78418 12.8934 10.0283C12.8934 10.2724 12.7964 10.5065 12.6238 10.6791ZM15.5388 11.6508L14.888 12.2925C14.7126 12.4426 14.487 12.5211 14.2563 12.5122C14.0256 12.5033 13.8068 12.4076 13.6435 12.2444C13.4803 12.0811 13.3846 11.8623 13.3757 11.6316C13.3668 11.4009 13.4453 11.1753 13.5955 11L14.2463 10.3491C14.5903 10.0051 14.7836 9.53855 14.7836 9.05204C14.7836 8.56554 14.5903 8.09897 14.2463 7.75496C13.9023 7.41095 13.4357 7.21769 12.9492 7.21769C12.4627 7.21769 11.9961 7.41095 11.6521 7.75496L11.0013 8.40579C10.8259 8.55597 10.6004 8.63444 10.3697 8.62553C10.139 8.61662 9.92013 8.52098 9.75687 8.35773C9.59362 8.19447 9.49798 7.97563 9.48907 7.74493C9.48016 7.51422 9.55863 7.28865 9.70881 7.11329L10.3505 6.46246C11.0385 5.77445 11.9716 5.38792 12.9446 5.38792C13.9176 5.38792 14.8508 5.77445 15.5388 6.46246C16.2268 7.15048 16.6133 8.08363 16.6133 9.05663C16.6133 10.0296 16.2268 10.9628 15.5388 11.6508Z" fill="#15526D"/>
                </svg>
            `;
            document.body.appendChild(link);
        }

        state.deeplinkElement = link;

        link.addEventListener('mouseenter', () => {
            if (state.hideTimeout) {
                clearTimeout(state.hideTimeout);
                state.hideTimeout = null;
            }
        });

        link.addEventListener('mouseleave', () => {
            this._co_scheduleHide();
        });

        const hideOnScroll = (e) => {
            this._co_hideDeeplink();

            const container = document.getElementById('scorecard-view-scroll-container');
            if (container && e.type === 'wheel') {
                container.scrollBy({
                    left: e.deltaX,
                    top: e.deltaY,
                    behavior: 'auto'
                });
            }
        };

        link.addEventListener('wheel', hideOnScroll, { passive: true });
        link.addEventListener('touchmove', hideOnScroll, { passive: true });
    },

    // ── Drilldown menu element creation ──

    _co_createMenuElement() {
        let container = document.getElementById('scorecard-drilldown-menu-overlay');
        if (!container) {
            container = document.createElement('div');
            container.id = 'scorecard-drilldown-menu-overlay';
            container.className = 'fixed pointer-events-none opacity-0 transition-opacity duration-150 z-10';
            container.style.cssText = 'will-change: opacity, top, left;';

            // 3-dots trigger button
            const trigger = document.createElement('button');
            trigger.type = 'button';
            trigger.className = 'cursor-pointer';
            trigger.style.cssText = 'opacity: 0.7; transition: opacity 150ms;';
            trigger.title = this._co_strings.options;
            trigger.setAttribute('aria-label', this._co_strings.options);
            trigger.setAttribute('aria-haspopup', 'menu');
            trigger.setAttribute('aria-expanded', 'false');
            trigger.setAttribute('aria-controls', 'scorecard-drilldown-menu');
            trigger.innerHTML = `
                <svg width="18" height="18" viewBox="0 0 4 16" fill="#15526D" xmlns="http://www.w3.org/2000/svg" style="pointer-events: none;" aria-hidden="true">
                    <circle cx="2" cy="2" r="1.5"/>
                    <circle cx="2" cy="8" r="1.5"/>
                    <circle cx="2" cy="14" r="1.5"/>
                </svg>
            `;

            trigger.addEventListener('mouseenter', () => { trigger.style.opacity = '1'; });
            trigger.addEventListener('mouseleave', () => { trigger.style.opacity = '0.7'; });
            container.appendChild(trigger);

            // Dropdown menu
            // One overlay exists per page (the getElementById guard above), so a fixed id is
            // unique and the trigger's aria-controls can name it.
            const dropdown = document.createElement('div');
            dropdown.id = 'scorecard-drilldown-menu';
            dropdown.className = 'absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-sm';
            dropdown.setAttribute('role', 'menu');
            dropdown.setAttribute('aria-label', this._co_strings.cellOptions);
            dropdown.style.display = 'none';

            const menuItem = document.createElement('button');
            menuItem.type = 'button';
            menuItem.setAttribute('role', 'menuitem');
            menuItem.tabIndex = -1;
            menuItem.className = 'w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-50 flex items-center gap-2 cursor-pointer';

            const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            icon.setAttribute('class', 'w-3.5 h-3.5 text-gray-400');
            icon.setAttribute('viewBox', '0 0 20 20');
            icon.setAttribute('fill', 'currentColor');
            icon.setAttribute('aria-hidden', 'true');
            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('fill-rule', 'evenodd');
            path.setAttribute('d', 'M10 3a1 1 0 011 1v10.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z');
            path.setAttribute('clip-rule', 'evenodd');
            icon.appendChild(path);
            menuItem.appendChild(icon);

            const label = document.createElement('span');
            label.textContent = this._co_strings.drilldownToDetails;
            menuItem.appendChild(label);

            menuItem.addEventListener('click', (e) => {
                e.stopPropagation();
                this._co_handleDrilldownClick();
            });

            dropdown.appendChild(menuItem);
            container.appendChild(dropdown);
            document.body.appendChild(container);

            state.triggerElement = trigger;
            state.dropdownElement = dropdown;

            trigger.addEventListener('click', (e) => {
                e.stopPropagation();
                if (state.menuOpen) {
                    this._co_closeDropdown();
                } else {
                    this._co_openDropdown();
                }
            });
        }

        state.menuElement = container;

        container.addEventListener('mouseenter', () => {
            if (state.hideTimeout) {
                clearTimeout(state.hideTimeout);
                state.hideTimeout = null;
            }
        });

        container.addEventListener('mouseleave', () => {
            if (!state.menuOpen) {
                this._co_scheduleHide();
            }
        });

        const hideOnScroll = (e) => {
            this._co_hideMenu();

            const scrollContainer = document.getElementById('scorecard-view-scroll-container');
            if (scrollContainer && e.type === 'wheel') {
                scrollContainer.scrollBy({
                    left: e.deltaX,
                    top: e.deltaY,
                    behavior: 'auto'
                });
            }
        };

        container.addEventListener('wheel', hideOnScroll, { passive: true });
        container.addEventListener('touchmove', hideOnScroll, { passive: true });
    },

    // ── Shared event listeners (single mouseover/mouseout for both features) ──

    _co_setupEventListeners() {
        state.boundHandleCellMouseOver = this._co_handleCellMouseOver.bind(this);
        state.boundHandleCellMouseOut = this._co_handleCellMouseOut.bind(this);
        state.boundHandleScroll = this._co_hideAll.bind(this);
        state.boundHandleClickOutside = this._co_handleClickOutside.bind(this);
        state.boundHandleKeydown = this._co_handleKeydown.bind(this);

        document.body.addEventListener('mouseover', state.boundHandleCellMouseOver);
        document.body.addEventListener('mouseout', state.boundHandleCellMouseOut);
        document.addEventListener('scroll', state.boundHandleScroll, true);
        document.addEventListener('mousedown', state.boundHandleClickOutside);
        document.addEventListener('keydown', state.boundHandleKeydown);
    },

    _co_handleCellMouseOver(event) {
        // Don't move overlays while drilldown dropdown is open
        if (state.menuOpen) return;

        const cell = event.target.closest('[data-col-idx]');
        if (!cell) return;
        if (state.currentHoveredCell === cell) return;

        if (state.intentTimeout) {
            clearTimeout(state.intentTimeout);
            state.intentTimeout = null;
        }

        // Hide overlays from previous cell immediately
        this._co_hideDeeplink();
        this._co_hideMenu();

        state.intentConfirmed = false;
        state.currentHoveredCell = cell;

        state.intentTimeout = setTimeout(() => {
            state.intentConfirmed = true;
            this._co_showOverlaysForCell(cell);
        }, state.INTENT_DELAY);
    },

    _co_handleCellMouseOut(event) {
        if (state.menuOpen) return;

        const cell = event.target.closest('[data-col-idx]');
        if (!cell || state.currentHoveredCell !== cell) return;

        const relatedTarget = event.relatedTarget;
        const isLeavingToOverlay = relatedTarget && (
            relatedTarget.id === 'scorecard-deeplink-overlay' ||
            relatedTarget.closest('#scorecard-deeplink-overlay') ||
            relatedTarget.id === 'scorecard-drilldown-menu-overlay' ||
            relatedTarget.closest('#scorecard-drilldown-menu-overlay')
        );
        const isLeavingToAnotherCell = relatedTarget && relatedTarget.closest('[data-col-idx]');

        if (isLeavingToOverlay || isLeavingToAnotherCell) return;

        if (state.intentTimeout) {
            clearTimeout(state.intentTimeout);
            state.intentTimeout = null;
        }

        this._co_scheduleHide();
    },

    // ── Show overlays for a cell (both deeplink and drilldown if applicable) ──

    _co_showOverlaysForCell(cell) {
        const rect = cell.getBoundingClientRect();

        // Show deeplink if cell has a valid deeplink, otherwise hide it
        const deeplink = cell.dataset.deeplink;
        if (deeplink && deeplink !== '' && deeplink !== 'null' && deeplink !== 'undefined') {
            const link = state.deeplinkElement;
            if (link) {
                link.href = deeplink;
                link.style.top = `${rect.top + (rect.height / 2) - 11}px`;
                link.style.left = `${rect.right - 48}px`;
                link.classList.remove('pointer-events-none', 'opacity-0');
                link.classList.add('pointer-events-auto', 'opacity-100');
                state.currentDeeplink = deeplink;
            }
        } else {
            this._co_hideDeeplink();
        }

        // Show drilldown menu if metric has drilldown, otherwise hide it
        const metricId = cell.dataset.metricId;
        const metric = metricId ? this.fullScorecardData?.metrics?.[metricId] : null;
        const startDate = cell.dataset.startDate;
        if (metric?.has_drilldown && metric.data?.[this.activeInterval]?.[startDate]) {
            const menu = state.menuElement;
            if (menu) {
                menu.style.top = `${rect.top + (rect.height / 2) - 9}px`;
                menu.style.left = `${rect.right - 20}px`;
                menu.classList.remove('pointer-events-none', 'opacity-0');
                menu.classList.add('pointer-events-auto', 'opacity-100');
            }
        } else {
            this._co_hideMenu();
        }
    },

    // ── Drilldown dropdown open/close ──

    _co_openDropdown() {
        if (!state.dropdownElement || !state.triggerElement) return;
        state.menuOpen = true;
        state.dropdownElement.style.display = '';
        state.triggerElement.setAttribute('aria-expanded', 'true');

        const firstItem = state.dropdownElement.querySelector('[role="menuitem"]');
        if (firstItem) firstItem.focus();

        if (state.hideTimeout) {
            clearTimeout(state.hideTimeout);
            state.hideTimeout = null;
        }
    },

    _co_closeDropdown() {
        if (!state.dropdownElement || !state.triggerElement) return;
        state.menuOpen = false;
        state.dropdownElement.style.display = 'none';
        state.triggerElement.setAttribute('aria-expanded', 'false');
    },

    _co_handleClickOutside(event) {
        if (!state.menuOpen) return;
        const menu = state.menuElement;
        if (menu && !menu.contains(event.target)) {
            this._co_closeDropdown();
            this._co_scheduleHide();
        }
    },

    _co_handleKeydown(event) {
        if (event.key === 'Escape' && state.menuOpen) {
            event.stopPropagation();
            // _co_openDropdown() moves focus into the menu, so Escape has to put it back: an APG
            // menu that closes without restoring focus leaves a keyboard user on document.body.
            // Not done in _co_closeDropdown(): a click outside, or the drilldown action itself,
            // both close the menu with focus deliberately elsewhere.
            const trigger = state.triggerElement;
            this._co_closeDropdown();
            if (trigger) trigger.focus();
            this._co_scheduleHide();
        }
    },

    _co_handleDrilldownClick() {
        const cell = state.currentHoveredCell;
        if (!cell) return;

        const metricId = parseInt(cell.dataset.metricId, 10);
        const metricName = cell.dataset.rowName || '';
        const startDate = cell.dataset.startDate;
        let endDate = cell.dataset.endDate;

        // If endDate is in the future, use today's date in workspace timezone
        if (endDate) {
            const today = workspaceDateString(this.workspaceTimezone);

            if (endDate > today) {
                endDate = today;
            }
        }

        this._co_closeDropdown();
        this._co_hideAll();

        if (window.Livewire) {
            // The pressed item is hidden with its dropdown, and the Options button with the
            // overlay, so the drilldown's trap has nothing to give focus back to and it falls to
            // <body>, at the top of the page. Once the drilldown closes, the cell gets it instead.
            const cellId = cell.id;
            returnFocusAfterDrilldown(() => this._co_focusTargetForCell(cellId));
            Livewire.dispatch(DRILLDOWN_EVENT, { metricId, metricName, startDate, endDate });
        }
    },

    // Where focus goes when a drilldown opened from the cell `cellId` names closes: that cell's
    // Options button, drawn on it again, so it is on screen and Enter opens the same menu; the cell
    // itself, made focusable, if it no longer offers a drilldown; nothing if it has left the page
    // (scrolled out of the virtual grid), so focus stays where it fell. Looked up by id, because the
    // grid can draw the cell anew while the drilldown is open.
    _co_focusTargetForCell(cellId) {
        const cell = cellId ? document.getElementById(cellId) : null;
        if (!cell) return null;

        state.currentHoveredCell = cell;
        state.intentConfirmed = true;
        this._co_showOverlaysForCell(cell);

        if (state.triggerElement && state.menuElement?.classList.contains('opacity-100')) {
            return state.triggerElement;
        }

        if (!cell.hasAttribute('tabindex')) cell.setAttribute('tabindex', '-1');

        return cell;
    },

    // ── Hide / schedule hide ──

    _co_scheduleHide() {
        if (state.hideTimeout) clearTimeout(state.hideTimeout);

        state.hideTimeout = setTimeout(() => {
            const link = state.deeplinkElement;
            const menu = state.menuElement;
            const cell = state.currentHoveredCell;

            if (link && link.matches(':hover')) return;
            if (menu && menu.matches(':hover')) return;
            if (cell && cell.matches(':hover')) return;

            this._co_hideAll();
        }, state.HIDE_DELAY);
    },

    _co_hideDeeplink() {
        const link = state.deeplinkElement;
        if (!link) return;

        link.classList.remove('pointer-events-auto', 'opacity-100');
        link.classList.add('pointer-events-none', 'opacity-0');
        state.currentDeeplink = null;
    },

    _co_hideMenu() {
        const menu = state.menuElement;
        if (!menu) return;

        this._co_closeDropdown();
        menu.classList.remove('pointer-events-auto', 'opacity-100');
        menu.classList.add('pointer-events-none', 'opacity-0');
    },

    _co_hideAll() {
        this._co_hideDeeplink();
        this._co_hideMenu();
        this._co_clearTimeouts();
        state.currentHoveredCell = null;
        state.intentConfirmed = false;
    },

    _co_clearTimeouts() {
        if (state.intentTimeout) {
            clearTimeout(state.intentTimeout);
            state.intentTimeout = null;
        }
        if (state.hideTimeout) {
            clearTimeout(state.hideTimeout);
            state.hideTimeout = null;
        }
    }
});
